import React, { useEffect, useState } from "react";
import {
  BrowserRouter, Navigate, Route, Routes, useParams, useNavigate, useLocation,
} from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import CartDrawer from "./components/CartDrawer";
import FloatingContactCTA from "./components/FloatingContactCTA";

import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ContactPage from "./pages/ContactPage";
import CartPage from "./pages/CartPage";
import StoryPage from "./pages/StoryPage";
import DestinyPage from "./pages/DestinyPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RegisterSuccessPage from "./pages/RegisterSuccessPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import AdminProductCategoriesPage from "./pages/AdminProductCategoriesPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminContactLeadsPage from "./pages/AdminContactLeadsPage";
import AccountPage from "./pages/AccountPage";
import StaffDashboard from "./pages/StaffDashboard";
import {
  getKingJadesSubcategory,
  getKingJadesProductPath,
  loadDynamicProductMenu,
} from "./config/productMenuConfig";
import { productApi } from "./api/productApi";

// In both /vongngocthiennhien/:slug and /trangsucngoc/:slug,
// a configured child-category slug opens the catalog; any other slug opens product detail.
// ProductDetailPage keeps using useParams().slug and needs no data-fetching changes.
const CatalogOrProductRoute: React.FC<{ parentId: string }> = ({ parentId }) => {
  const { slug = "" } = useParams<{ slug: string }>();
  const [menuLoaded, setMenuLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    setMenuLoaded(false);
    // Menu được merge từ API, nên cần đọc xong để biết đây là
    // danh mục con động hay slug sản phẩm (tránh mở nhầm 404).
    void loadDynamicProductMenu("public")
      .catch(() => { /* Đã có fallback LEGACY_PRODUCT_MENU. */ })
      .finally(() => { if (active) setMenuLoaded(true); });
    return () => { active = false; };
  }, [parentId, slug]);

  const subcategory = getKingJadesSubcategory(parentId, slug);
  if (subcategory) return <ProductPage />;
  if (!menuLoaded) return <div role="status" style={{ minHeight: "35vh" }} />;
  return <ProductDetailPage />;
};

// Keeps old in-app /products/:slug links functional and updates their address.
// Direct browser hits are HTTP-301 redirected by SeoProductController on production.
const LegacyProductDetailRoute: React.FC = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!slug) return;
    let active = true;
    void productApi.getPublicProductDetail(slug).then((response) => {
      if (!active) return;
      const raw = response as unknown as {
        product?: { slug?: string; parentCategoryId?: string; parentCategoryName?: string };
        slug?: string; parentCategoryId?: string; parentCategoryName?: string;
      };
      const product = raw?.product || raw;
      if (!product?.slug) return;
      const target = getKingJadesProductPath({ ...product, slug: product.slug });
      if (target !== location.pathname) {
        navigate(target, { replace: true, state: location.state });
      }
    }).catch(() => { /* Existing ProductDetailPage displays any product API error. */ });
    return () => { active = false; };
  }, [slug, navigate, location.pathname, location.state]);
  return <ProductDetailPage />;
};

const App: React.FC = () => (
  <AuthProvider>
    <CartProvider>
      <BrowserRouter>
        <CartDrawer />
        <FloatingContactCTA />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cau-chuyen-thuong-hieu" element={<StoryPage />} />
          <Route path="/products" element={<ProductPage />} />
          <Route path="/vongngocthiennhien" element={<ProductPage />} />
          <Route path="/trangsucngoc" element={<ProductPage />} />
          <Route path="/vongngocthiennhien/:slug" element={<CatalogOrProductRoute parentId="vong-ngoc-thien-nhien" />} />
          <Route path="/trangsucngoc/:slug" element={<CatalogOrProductRoute parentId="trang-suc-ngoc" />} />
          <Route path="/products/:slug" element={<LegacyProductDetailRoute />} />
          <Route path="/xem-menh" element={<DestinyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register-success" element={<RegisterSuccessPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<NewsDetailPage />} />
          <Route path="/admin-dashboard" element={
            <ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/accounts" element={
            <ProtectedRoute roles={["admin"]}><AccountPage /></ProtectedRoute>
          } />
          <Route path="/admin/product-categories" element={
            <ProtectedRoute roles={["admin"]}><AdminProductCategoriesPage /></ProtectedRoute>
          } />
          <Route path="/admin/accounts/create" element={<Navigate to="/admin/accounts" replace />} />
          <Route path="/admin/contact-leads" element={
            <ProtectedRoute roles={["admin", "staff"]}><AdminContactLeadsPage /></ProtectedRoute>
          } />
          <Route path="/staff" element={
            <ProtectedRoute roles={["admin", "staff"]}><StaffDashboard /></ProtectedRoute>
          } />
          <Route path="/user" element={
            <ProtectedRoute roles={["admin", "staff", "user"]}>
              <Navigate to="/" replace />
            </ProtectedRoute>
          } />
          <Route path="/unauthorized" element={
            <div className="kj-unauthorized-page">
              <h1>Không có quyền truy cập</h1>
              <p>Tài khoản của bạn không được phép vào trang này.</p>
            </div>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </CartProvider>
  </AuthProvider>
);

export default App;
